// Receipts for parts bought (BL-012): what the office approves, beside the bills and on the job. A
// receipt on the supplier's account becomes a vendor bill; one on a company card posts to the card.
// The amounts are cost (Q38): `amounts` carries them to the recorder and to payables.

export interface JobPurchase {
  id: string;
  number: string;
  kind: 'purchase' | 'credit';
  creditOfId: string | null;
  jobId: string | null;
  jobNumber: string | null;
  staffName: string | null;
  vendorId: string | null;
  vendorName: string | null;
  supplierName: string | null;
  receiptRef: string | null;
  purchasedOn: string;
  totalCents?: number;
  costCents?: number;
  taxes: { code: string; taxCents?: number }[];
  lines: { description: string; quantity: string; amountCents?: number | null }[];
  amounts: { totalCents: number; costCents: number; taxes: { code: string; taxCents: number }[] } | null;
  note: string | null;
  source: 'tech' | 'office' | 'scan';
  status: 'submitted' | 'approved' | 'void' | 'imported';
  paidHow: 'on_account' | 'company_card' | null;
  vendorBillId: string | null;
  voidReason: string | null;
  receipt: { fileId: string; kind: string; status: string; url: string | null } | null;
  rowVersion: number;
}

/** Where it was bought: the vendor picked, else the store's name as typed. */
export const supplierText = (p: Pick<JobPurchase, 'vendorName' | 'supplierName'>) =>
  p.vendorName ?? p.supplierName ?? 'Unknown store';

/** "Job 1042", or "Overhead" for one on no job. */
export const whereText = (p: Pick<JobPurchase, 'jobNumber' | 'jobId'>) =>
  p.jobId ? `Job ${p.jobNumber ?? ''}`.trim() : 'Overhead';

/** The total as the reader may see it (Q38): their own copy, else the tagged field, else none. */
export const totalOf = (p: Pick<JobPurchase, 'amounts' | 'totalCents'>): number | null =>
  p.amounts?.totalCents ?? p.totalCents ?? null;

/** The recoverable tax recorded on it, as the approval form starts from. */
export function recordedTax(p: Pick<JobPurchase, 'amounts' | 'taxes'>, code = 'GST'): number | null {
  const t = (p.amounts?.taxes ?? p.taxes).find((x) => x.code === code);
  return t?.taxCents ?? null;
}

export function statusText(p: Pick<JobPurchase, 'status' | 'paidHow' | 'kind'>): string {
  if (p.status === 'void') return 'Voided';
  if (p.status === 'submitted') return 'Waiting for approval';
  if (p.status === 'imported') return 'From before Flowology: the job’s cost, never posted';
  const what = p.kind === 'credit' ? 'Credit approved' : 'Approved';
  return p.paidHow === 'on_account' ? `${what}: on the supplier’s account` : `${what}: company card`;
}

/** The receipt photo's state, for the line under the card. */
export function receiptText(p: Pick<JobPurchase, 'receipt'>): string {
  if (!p.receipt) return 'No receipt photo';
  if (p.receipt.status === 'verified')
    return p.receipt.kind === 'document' ? 'Receipt (PDF)' : 'Receipt photo';
  if (p.receipt.status === 'discarded') return 'No receipt photo';
  return 'Receipt photo still uploading';
}

export interface ApproveForm {
  paidHow: 'on_account' | 'company_card';
  vendorId: string;
  bankAccountId: string;
  accountId: string;
  /** Recoverable GST in cents, as confirmed; null keeps what was recorded. */
  gstCents: number | null;
  receiptRef: string;
}

/** The form a receipt opens with: on the supplier's account when it names a vendor. */
export function approveDefaults(
  p: Pick<JobPurchase, 'vendorId' | 'receiptRef' | 'amounts' | 'taxes'>,
  cards: readonly { id: string }[],
): ApproveForm {
  return {
    paidHow: p.vendorId || !cards.length ? 'on_account' : 'company_card',
    vendorId: p.vendorId ?? '',
    bankAccountId: cards[0]?.id ?? '',
    accountId: '',
    gstCents: recordedTax(p),
    receiptRef: p.receiptRef ?? '',
  };
}

/** What's missing before it can go in; null when it can be approved. */
export function approveBlocker(p: Pick<JobPurchase, 'jobId'>, f: ApproveForm): string | null {
  if (f.paidHow === 'on_account' && !f.vendorId) return 'Pick the vendor whose account it’s on.';
  if (f.paidHow === 'company_card' && !f.bankAccountId) return 'Pick the company card it was paid with.';
  if (!p.jobId && !f.accountId) return 'Pick the account this overhead goes to.';
  return null;
}

/** jobPurchases.approve's input from the form. */
export function approveInput(p: Pick<JobPurchase, 'id' | 'rowVersion'>, f: ApproveForm) {
  return {
    purchaseId: p.id,
    rowVersion: p.rowVersion,
    paidHow: f.paidHow,
    ...(f.paidHow === 'company_card' ? { bankAccountId: f.bankAccountId } : {}),
    ...(f.vendorId ? { vendorId: f.vendorId } : {}),
    ...(f.accountId ? { accountId: f.accountId } : {}),
    ...(f.gstCents !== null ? { taxes: f.gstCents > 0 ? [{ code: 'GST', taxCents: f.gstCents }] : [] } : {}),
    ...(f.receiptRef.trim() ? { receiptRef: f.receiptRef.trim() } : {}),
  };
}

/** Company cards: bank accounts on a credit card account in the chart. */
export const cardsOf = <B extends { id: string; accountId: string }>(
  banks: readonly B[],
  accounts: readonly { id: string; subtype: string }[],
) => {
  const cardGl = new Set(accounts.filter((a) => a.subtype === 'credit_card').map((a) => a.id));
  return banks.filter((b) => cardGl.has(b.accountId));
};

/** Accounts an overhead receipt may go to: running costs and costs of jobs. */
export const overheadAccounts = <T extends { subtype: string; active: boolean }>(accounts: readonly T[]) =>
  accounts.filter((a) => a.active && (a.subtype === 'expense' || a.subtype === 'cost_of_sales'));
